import { uploadSellerImage } from "../../lib/imageUploads";
import { tr, usePartnerTranslation } from "../../lib/language";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { parseApiError } from "../../lib/api";
import { useNavigate, useBlocker } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Save,
  Sparkles,
  CheckCircle2,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../../components/ui/dialog";
import { cn } from "../../lib/utils";
import { useAuth } from "../../lib/auth";
import { findRestrictedCountryName } from "../../lib/constants";
import WizardStepper from "../../components/portal/create/WizardStepper";
import {
  STEPS,
  initialFormState,
  TITLE_MIN,
  TITLE_MAX,
  SHORT_DESC_MIN,
  SHORT_DESC_MAX,
  FULL_DESC_MIN,
  FULL_DESC_MAX,
  HIGHLIGHT_MIN,
  HIGHLIGHT_MAX,
  HIGHLIGHT_COUNT,
  VISIT_LOCATIONS_MIN,
  INCLUSIONS_TEXT_MAX,
  PHOTO_MIN_COUNT,
  REFUND_POLICY_UNITS,
  REFUND_POLICY_OPTIONS,
  createBlankOption,
} from "../../components/portal/create/wizardConstants";
import Step0Entry from "../../components/portal/create/Step0Entry";
import StepAcknowledgment from "../../components/portal/create/StepAcknowledgment";
import Step1Basics from "../../components/portal/create/Step1Basics";
import Step3Visuals from "../../components/portal/create/Step3Visuals";
import Step2Option from "../../components/portal/create/Step2Option";
import Step5Review, {
  isPublishReady,
  missingPublishItems,
} from "../../components/portal/create/Step5Review";
import { useLegalAccount } from "../../lib/legal";
import { mealsFromApi, mealsToApi } from "../../lib/meals";
import {
  optionContactErrors,
  productBasicsContactErrors,
} from "../../lib/productTextSafety";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const RESTRICTED_COUNTRY_ERROR = tr("This country is not supported for Voocly supplier products.");
const PRODUCT_REFERENCE_CODE_MAX = 10;
const PRIMARY_CITY_MAX = 120;
const OPTION_CAPACITY_MAX = 999;
const PRICE_MAX = 9999999.99;
const VALIDATION_ERROR_SELECTOR =
  '[data-error-anchor="true"], [data-testid="options-empty-state"]';
const FOCUSABLE_SELECTOR =
  'input:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const effectiveTierTo = (tiers, index, capacityMax) => {
  const raw = tiers?.[index]?.to;
  if (raw !== "" && raw !== null && raw !== undefined) return raw;

  const capMax = Number(capacityMax);
  if (index === (tiers || []).length - 1 && Number.isFinite(capMax) && capMax > 0) {
    return capMax;
  }

  return raw;
};

const tierValidationError = (tiers, capacityMax) => {
  if (!Array.isArray(tiers) || tiers.length === 0) {
    return tr("Add at least one tier");
  }

  const missingMax = tiers.some((tier, index) => {
    const to = Number(effectiveTierTo(tiers, index, capacityMax));
    return !(to > 0);
  });
  if (missingMax) return tr("Set a maximum people limit for every tier");

  const missingPrice = tiers.some((tier) => !(Number(tier?.price) > 0));
  if (missingPrice) return tr("Set a price for every tier");

  const overLimitPrice = tiers.some((tier) => Number(tier?.price) > PRICE_MAX);
  if (overLimitPrice) return tr("Price cannot exceed 9,999,999.99");

  return "";
};

const toLocalDateString = (date) => date.toLocaleDateString("en-CA");

const addYears = (base, years) => {
  const next = new Date(base);
  next.setFullYear(next.getFullYear() + years);
  return next;
};

const normaliseBulletLines = (value) =>
  String(value || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[•*-]\s*/, "").trim())
    .filter(Boolean)
    .join("\n");

const normalizeLoadedOption = (option) => {
  const base = createBlankOption();
  const normalized = {
    ...base,
    ...option,
    id: option.id || option.Id || undefined,
    name: option.name || option.Name || "",
    reference_code: option.reference_code || option.referenceCode || option.ReferenceCode || "",
    languages: Array.isArray(option.languages || option.Languages)
      ? option.languages || option.Languages
      : [],
    languages_other: option.languages_other || option.languagesOther || option.LanguagesOther || "",
    privacy_type: option.privacy_type || option.privacyType || option.PrivacyType || "",
    duration_or_validity: option.duration_or_validity || option.durationOrValidity || option.DurationOrValidity || "",
    validity_type: option.validity_type || option.validityType || option.ValidityType || "",
    duration_unit: option.duration_unit || option.durationUnit || option.DurationUnit || base.duration_unit,
    duration_value: option.duration_value ?? option.durationValue ?? option.DurationValue ?? "",
    meeting_point_type: option.meeting_point_type || option.meetingPointType || option.MeetingPointType || "",
    meeting_point_location: option.meeting_point_location || option.meetingPointLocation || option.MeetingPointLocation || "",
    meeting_point_coords: option.meeting_point_coords ?? option.meetingPointCoords ?? option.MeetingPointCoords ?? null,
    meeting_point_description: option.meeting_point_description || option.meetingPointDescription || option.MeetingPointDescription || "",
    pickup_area_description: option.pickup_area_description || option.pickupAreaDescription || option.PickupAreaDescription || "",
    pickup_transport_mode: option.pickup_transport_mode || option.pickupTransportMode || option.PickupTransportMode || "",
    schedule_start_date: option.schedule_start_date || option.scheduleStartDate || option.ScheduleStartDate || "",
    has_schedule_end_date: !!(option.has_schedule_end_date ?? option.hasScheduleEndDate ?? option.HasScheduleEndDate ?? false),
    schedule_end_date: option.schedule_end_date || option.scheduleEndDate || option.ScheduleEndDate || "",
    availability_mode: option.availability_mode || option.availabilityMode || option.AvailabilityMode || "",
    schedule_slots: Array.isArray(option.schedule_slots || option.scheduleSlots || option.ScheduleSlots)
      ? option.schedule_slots || option.scheduleSlots || option.ScheduleSlots
      : [],
    cutoff_minutes: option.cutoff_minutes ?? option.cutoffMinutes ?? option.CutoffMinutes ?? base.cutoff_minutes,
    pricing_basis: option.pricing_basis || option.pricingBasis || option.PricingBasis || "",
    pricing_mode: option.pricing_mode || option.pricingMode || option.PricingMode || "",
    capacity_min: option.capacity_min ?? option.capacityMin ?? option.CapacityMin ?? base.capacity_min,
    capacity_max: option.capacity_max ?? option.capacityMax ?? option.CapacityMax ?? "",
    flat_tiers: Array.isArray(option.flat_tiers || option.flatTiers || option.FlatTiers)
      ? option.flat_tiers || option.flatTiers || option.FlatTiers
      : base.flat_tiers,
    categories: Array.isArray(option.categories || option.Categories)
      ? option.categories || option.Categories
      : base.categories,
    group_sizes: Array.isArray(option.group_sizes || option.groupSizes || option.GroupSizes)
      ? option.group_sizes || option.groupSizes || option.GroupSizes
      : base.group_sizes,
    group_prices: option.group_prices || option.groupPrices || option.GroupPrices || base.group_prices,
    currency: option.currency || option.Currency || "",
  };
  return normalized;
};

/* Map a backend product doc into the wizard form state.
 * - mode='clone'  → prefix title with "Cloned: ", set cloned_from
 * - mode='edit'   → preserve title and existing identity exactly
 */
const productToForm = (product, mode = "clone") => {
  const base = initialFormState();
  const isEdit = mode === "edit";
  return {
    ...base,
    product_type: product.product_type || base.product_type,
    start_from: product.start_from || base.start_from,
    title: isEdit
      ? (product.title || "")
      : product.title
        ? `Cloned: ${product.title}`
        : "",
    reference_code: (product.reference_code || "").slice(0, PRODUCT_REFERENCE_CODE_MAX),
    primary_city: product.primary_city || "",
    short_description: product.short_description || "",
    full_description: product.full_description || "",
    highlights:
      Array.isArray(product.highlights) && product.highlights.length === HIGHLIGHT_COUNT
        ? product.highlights
        : ["", "", ""],
    visit_locations: product.visit_locations || [],
    keywords: product.keywords || [],
    experience_leader: product.experience_leader || "",
    inclusions_tags: product.inclusions_tags || [],
    inclusions_other: product.inclusions_other || "",
    not_included_tags: product.not_included_tags || [],
    not_included_other: product.not_included_other || "",
    meals: mealsFromApi(product.meals),
    transports: product.transports || [],
    not_suitable_for: product.not_suitable_for || [],
    suitability_tags: product.suitability_tags || [],
    guest_must_know: product.guest_must_know || "",
    refund_policy_type: product.refund_policy_type || base.refund_policy_type,
    custom_refund_policy_value: product.custom_refund_policy_value || "",
    custom_refund_policy_unit: product.custom_refund_policy_unit || base.custom_refund_policy_unit,
    photos: Array.isArray(product.photo_keys)
      ? product.photo_keys.map((key, i) => ({
          key,
          url: product.photo_urls?.[i] ?? key,
          name: key.split("/").pop() ?? key,
        }))
      : Array.isArray(product.photos)
        ? product.photos
        : [],
    copyright_confirmed: !!product.copyright_confirmed,
    compliance_certified: isEdit ? !!product.compliance_certified : false,
    options:
      Array.isArray(product.options) && product.options.length > 0
        ? product.options.map(normalizeLoadedOption)
        : base.options,
    cloned_from: isEdit ? product.cloned_from || null : product.id,
  };
};

const SuccessModal = ({ open, kind, mode, onClose, onGoToProducts }) => {
  usePartnerTranslation();
  if (!open) return null;
  const isPublished = kind === "active";
  const isDraft = kind === "draft";
  const isEdit = mode === "edit";
  const title = isEdit
    ? isPublished
      ? tr("Changes published")
      : tr("Changes saved")
    : isPublished
      ? tr("You're live!")
      : tr("Draft saved");
  const description = isEdit
    ? isPublished
      ? tr("Your edits are now live on the customer site.")
      : tr("Your draft was updated. Open it from the Products page anytime to keep editing.")
    : isPublished
      ? tr("Your product is now visible on the customer site. Customers can book it right away.")
      : tr("Your draft is safely stored. Open it from the Products page to keep editing.");
  return (
    <Dialog open onOpenChange={(v) => !v && !isDraft && onClose()}>
      <DialogContent
        data-testid="create-success-modal"
        className="max-w-[480px] rounded-2xl border-[#EFE4E4] p-0"
        showClose={!isDraft}
        onEscapeKeyDown={isDraft ? (event) => event.preventDefault() : undefined}
        onPointerDownOutside={isDraft ? (event) => event.preventDefault() : undefined}
        onInteractOutside={isDraft ? (event) => event.preventDefault() : undefined}
      >
        <DialogHeader className="border-b border-[#EFE4E4] px-6 py-5">
          <DialogTitle className="flex items-center gap-2 text-lg font-extrabold tracking-tight text-[#0F1115]">
            <CheckCircle2 className="h-5 w-5 text-[#0F6B2B]" strokeWidth={1.8} />
            {title}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6B7280]">
            {description}
          </DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-2 border-t border-[#EFE4E4] px-6 py-4">
          {!isEdit && !isDraft && (
            <button
              type="button"
              data-testid="success-close"
              onClick={onClose}
              className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]"
            >{tr("Close")}</button>
          )}
          <button
            type="button"
            data-testid="success-go-to-products"
            onClick={onGoToProducts}
            className="humrahe-btn-primary inline-flex h-10 items-center rounded-md px-5 text-sm font-semibold"
          >{tr("Back to all products")}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ConfirmCancelModal = ({ open, onDismiss, onDiscard, onSave, saving, isEdit }) => {
  usePartnerTranslation();
  if (!open) return null;
  return (
    <Dialog open onOpenChange={(v) => !v && !saving && onDismiss()}>
      <DialogContent
        data-testid="cancel-create-modal"
        className="max-w-[440px] rounded-2xl border-[#EFE4E4] p-0"
      >
        <DialogHeader className="border-b border-[#EFE4E4] px-6 py-4">
          <DialogTitle className="text-lg font-extrabold tracking-tight text-[#0F1115]">
            {isEdit ? tr("Save your changes?") : tr("Save as draft?")}
          </DialogTitle>
          <DialogDescription className="text-sm text-[#6B7280]">{tr("You can continue editing this later")}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center justify-end gap-2 px-6 py-4">
          <button
            type="button"
            data-testid="cancel-leave"
            onClick={onDiscard}
            disabled={saving}
            className="inline-flex h-10 items-center rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#9B0000] transition-colors hover:border-[#9B0000] disabled:opacity-60"
          >{tr("Discard")}</button>
          <button
            type="button"
            data-testid="cancel-save-draft"
            onClick={onSave}
            disabled={saving}
            className="humrahe-btn-primary inline-flex h-10 items-center gap-2 rounded-md px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {isEdit ? tr("Save changes") : tr("Save as draft")}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function CreateProductPage({ mode = "create", productId = null }) {
  usePartnerTranslation();
  const navigate = useNavigate();
  const { role } = useAuth();
  const isEdit = mode === "edit";
  // In edit mode the Acknowledgment + Start steps are hidden, so the first
  // visible step is Basics (index 2).
  const firstStep = isEdit ? 2 : 0;
  const [step, setStep] = useState(firstStep);
  const [data, setData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [submittingStatus, setSubmittingStatus] = useState(null);
  const [successKind, setSuccessKind] = useState(null);
  // Pre-mark hidden upstream steps as completed in edit mode so the stepper
  // shows them as done (and lets the user jump back to Basics, etc).
  const [completed, setCompleted] = useState(() =>
    isEdit ? { acknowledge: true, start: true } : {},
  );
  const [touched, setTouched] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState("");
  const [editingStatus, setEditingStatus] = useState(null);
  const partnerCurrency = "USD";
  const [validationScrollVersion, setValidationScrollVersion] = useState(0);
  const { legal, loaded: legalLoaded } = useLegalAccount();
  const legalComplete = !!(legal && legal.exists);
  const canManageLegal = role === "administration" || !role;
  const legalPublishMessage = canManageLegal
    ? tr("Complete Legal info before publishing")
    : tr("Ask your account admin to fill the legal information to publish the product.");

  useEffect(() => {
    if (!validationScrollVersion) return undefined;

    const timer = window.setTimeout(() => {
      const target = document.querySelector(VALIDATION_ERROR_SELECTOR);
      if (!target) return;

      target.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusTarget = target.matches(FOCUSABLE_SELECTOR)
        ? target
        : target.querySelector(FOCUSABLE_SELECTOR);
      focusTarget?.focus?.({ preventScroll: true });
    }, 250);

    return () => window.clearTimeout(timer);
  }, [validationScrollVersion, errors, step]);

  /* Load existing product in edit mode */
  useEffect(() => {
    if (!isEdit || !productId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError("");
      try {
        const res = await axios.get(`${API}/products/${productId}`);
        if (cancelled) return;
        const product = res.data;
        setData(productToForm(product, "edit"));
        setEditingStatus(product.status || "draft");
        // Mark all upstream steps as completed so the user can jump around.
        setCompleted({
          acknowledge: true,
          start: true,
          basics: true,
          visuals: true,
          option: true,
        });
      } catch (err) {
        const { code, message } = parseApiError(err, tr("Couldn't load this product."));
        const msg = code === "not_found"
          ? "We couldn't find that product. It may have been deleted."
          : message;
        setLoadError(msg);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, productId]);

  const update = useCallback((key, value) => {
    setData((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => {
      if (prev[key]) {
        const { [key]: _, ...rest } = prev;
        return rest;
      }
      return prev;
    });
    setTouched(true);
  }, []);

  const hydrateFromProduct = useCallback((product) => {
    setData(() => productToForm(product, "clone"));
    setTouched(true);
  }, []);

  /* ---- Per-step gate validation ---- */
  const validateStep = (idx) => {
    const e = {};
    if (idx === 0) {
      // Acknowledgment is a read-only intro screen — no validation.
    } else if (idx === 1) {
      // Start — single product-type radio
      if (!data.product_type) e.product_type = tr("Pick a product type");
    } else if (idx === 2) {
      const titleLen = (data.title || "").length;
      if (titleLen < TITLE_MIN || titleLen > TITLE_MAX)
        e.title = tr("Title must be {{value0}}–{{value1}} characters", { value0: TITLE_MIN, value1: TITLE_MAX });
      const primaryCity = (data.primary_city || "").trim();
      if (!primaryCity)
        e.primary_city = tr("Primary city is required");
      else if (primaryCity.length > PRIMARY_CITY_MAX)
        e.primary_city = tr("Primary city must be {{value0}} characters or less", { value0: PRIMARY_CITY_MAX });
      const sLen = (data.short_description || "").length;
      if (sLen < SHORT_DESC_MIN || sLen > SHORT_DESC_MAX)
        e.short_description = tr("Short description must be {{value0}}–{{value1}} characters", { value0: SHORT_DESC_MIN, value1: SHORT_DESC_MAX });
      const fLen = (data.full_description || "").length;
      if (fLen < FULL_DESC_MIN || fLen > FULL_DESC_MAX)
        e.full_description = tr("Full description must be {{value0}}–{{value1}} characters", { value0: FULL_DESC_MIN, value1: FULL_DESC_MAX });
      const hList = (data.highlights || []).map((h) => (h || "").trim());
      const hErrors = hList.map((h) =>
        h.length < HIGHLIGHT_MIN || h.length > HIGHLIGHT_MAX
          ? `Must be ${HIGHLIGHT_MIN}–${HIGHLIGHT_MAX} characters`
          : null,
      );
      if (hErrors.some(Boolean)) {
        e.highlights = tr("All {{value0}} highlights are required ({{value1}}–{{value2}} chars each)", { value0: HIGHLIGHT_COUNT, value1: HIGHLIGHT_MIN, value2: HIGHLIGHT_MAX });
        e.highlights_list = hErrors;
      }
      if ((data.visit_locations || []).length < VISIT_LOCATIONS_MIN)
        e.visit_locations = tr("Add at least one location");
      else if ((data.visit_locations || []).some((location) => findRestrictedCountryName(location)))
        e.visit_locations = RESTRICTED_COUNTRY_ERROR;
      if ((data.keywords || []).length < 1)
        e.keywords = tr("Select at least one interest");
      else if ((data.keywords || []).length > 5)
        e.keywords = tr("Select no more than 5 interests");
      if (!data.experience_leader) e.experience_leader = tr("Pick a leader type");
      const refundPolicyType = data.refund_policy_type || "standard_24_hours";
      if (!REFUND_POLICY_OPTIONS.some((option) => option.value === refundPolicyType)) {
        e.refund_policy_type = tr("Pick a refund policy");
      }
      if (refundPolicyType === "custom") {
        const refundValue = Number(data.custom_refund_policy_value);
        if (!/^\d{1,2}$/.test(String(data.custom_refund_policy_value || "")) || refundValue < 1) {
          e.custom_refund_policy_value = tr("Enter a number from 1 to 99");
        }
        if (!REFUND_POLICY_UNITS.includes(data.custom_refund_policy_unit)) {
          e.custom_refund_policy_unit = tr("Pick a time unit");
        }
      }
      Object.assign(e, productBasicsContactErrors(data));
      if ((data.inclusions_other || "").length > INCLUSIONS_TEXT_MAX)
        e.inclusions_other = tr("What's included must be {{value0}} characters or less", { value0: INCLUSIONS_TEXT_MAX });
      if ((data.not_included_other || "").length > INCLUSIONS_TEXT_MAX)
        e.not_included_other = tr("What's not included must be {{value0}} characters or less", { value0: INCLUSIONS_TEXT_MAX });
    } else if (idx === 3) {
      if ((data.photos || []).length < PHOTO_MIN_COUNT)
        e.photos = tr("Add at least {{value0}} photos", { value0: PHOTO_MIN_COUNT });
      if (!data.copyright_confirmed)
        e.copyright_confirmed = tr("Please confirm the copyright statement");
    } else if (idx === 4) {
      const opts = data.options || [];
      const optionErrors = [];
      let hasErr = false;
      if (opts.length === 0) {
        // Surface a friendly error when the supplier hasn't created any
        // options at all yet — they can't progress past Options & pricing
        // without one.
        e.options_message = tr("Create at least one option");
      }
      opts.forEach((opt) => {
        const oe = {};
        const nameLen = (opt.name || "").trim().length;
        if (nameLen < 10 || nameLen > 60)
          oe.name = "Option name must be 10–60 characters";
        if (!(opt.languages || []).length)
          oe.languages = "Pick at least one language";
        if (!opt.privacy_type) oe.privacy_type = "Pick private or group";
        // Duration / Validity gate
        if (!opt.duration_or_validity) {
          oe.duration_or_validity = "Pick duration or validity";
        } else if (opt.duration_or_validity === "duration") {
          if (!opt.duration_unit) oe.duration_unit = "Pick a unit";
          if (!(Number(opt.duration_value) >= 1))
            oe.duration_value = "Duration must be 1 or more";
        } else if (opt.duration_or_validity === "validity") {
          if (!opt.validity_type) {
            oe.validity_type = "Pick a validity period";
          } else if (
            opt.validity_type === "from_booking" ||
            opt.validity_type === "from_activation"
          ) {
            if (!opt.duration_unit) oe.duration_unit = "Pick a unit";
            if (!(Number(opt.duration_value) >= 1))
              oe.duration_value = "Validity must be 1 or more";
          }
        }
        if (!opt.meeting_point_type) oe.meeting_point_type = "Pick a meeting type";
        if (
          opt.meeting_point_type === "set_meeting_point" &&
          !(opt.meeting_point_location || "").trim()
        )
          oe.meeting_point_location = "Address required";
        else if (
          opt.meeting_point_type === "set_meeting_point" &&
          findRestrictedCountryName(opt.meeting_point_location)
        )
          oe.meeting_point_location = RESTRICTED_COUNTRY_ERROR;
        if (
          opt.meeting_point_type === "pickup" &&
          !(opt.pickup_area_description || "").trim()
        )
          oe.pickup_area_description = "Pickup area required";
        else if (
          opt.meeting_point_type === "pickup" &&
          findRestrictedCountryName(opt.pickup_area_description)
        )
          oe.pickup_area_description = RESTRICTED_COUNTRY_ERROR;
        if (opt.meeting_point_type === "pickup" && !opt.pickup_transport_mode)
          oe.pickup_transport_mode = "Pick a transport mode";
        if (!opt.availability_mode)
          oe.availability_mode = "Pick how you set your availability";
        if ((opt.schedule_slots || []).length === 0)
          oe.schedule = "Add at least one time slot";
        // Schedule dates: start must be today or later on a brand-new product.
        // A saved or cloned product may carry a historical start date, and that
        // should not block moving through the wizard.
        const todayStr = new Date().toLocaleDateString("en-CA");
        const scheduleStartMax = toLocalDateString(addYears(new Date(), 1));
        const scheduleEndMax = toLocalDateString(addYears(new Date(), 10));
        if (
          !isEdit &&
          !data.cloned_from &&
          opt.schedule_start_date &&
          opt.schedule_start_date < todayStr
        ) {
          oe.schedule_start_date = "Start date can't be in the past";
        }
        if (opt.schedule_start_date && opt.schedule_start_date > scheduleStartMax) {
          oe.schedule_start_date = "Start date can't be more than 1 year in the future";
        }
        if (opt.has_schedule_end_date && !opt.schedule_end_date) {
          oe.schedule_end_date = "End date is required";
        }
        if (
          opt.schedule_end_date &&
          opt.schedule_start_date &&
          opt.schedule_end_date < opt.schedule_start_date
        ) {
          oe.schedule_end_date = "End date must be on or after the start date";
        }
        if (opt.schedule_end_date && opt.schedule_end_date > scheduleEndMax) {
          oe.schedule_end_date = "End date can't be more than 10 years in the future";
        }
        // ---- Pricing ----
        if (!opt.pricing_basis) {
          oe.pricing_basis = "Pick a pricing basis";
        } else if (opt.pricing_basis === "per_person") {
          if (!opt.pricing_mode) oe.pricing_mode = "Pick a price model";
          // Capacity
          const cMin = Number(opt.capacity_min);
          const cMax = Number(opt.capacity_max);
          if (!(cMin >= 1)) {
            oe.capacity = "Minimum capacity must be at least 1";
          } else if (cMax > OPTION_CAPACITY_MAX) {
            oe.capacity = tr("Maximum capacity cannot exceed {{value0}}", { value0: OPTION_CAPACITY_MAX });
          } else if (!(cMax >= cMin)) {
            oe.capacity = "Maximum capacity must be greater than or equal to minimum";
          }
          if (opt.pricing_mode === "flat") {
            const tiers = opt.flat_tiers || [];
            const tierError = tierValidationError(tiers, opt.capacity_max);
            if (tierError) oe.flat_tiers = tierError;
          } else if (opt.pricing_mode === "per_category") {
            const cats = opt.categories || [];
            const adult = cats.find((c) => c.key === "adult");
            const child = cats.find((c) => c.key === "child");
            const adultError = tierValidationError(adult?.tiers, opt.capacity_max);
            const childError = tierValidationError(child?.tiers, opt.capacity_max);
            if (adultError || childError)
              oe.categories = "Set a max tier number/price for both Adult and Child";
          }
        } else if (opt.pricing_basis === "per_group") {
          const sizes = (opt.group_sizes || []).filter(
            (s) => Number(s.to) > 0,
          );
          if (sizes.length === 0) {
            oe.group_sizes = "Add at least one group size";
          }
          // Each size must have a price set
          const missingPrice = sizes.find(
            (s) => !(Number((opt.group_prices || {})[String(Number(s.to))]) > 0),
          );
          if (sizes.length > 0 && missingPrice) {
            oe.group_prices = "Set a price for every group size";
          }
          const overLimitPrice = sizes.find(
            (s) => Number((opt.group_prices || {})[String(Number(s.to))]) > PRICE_MAX,
          );
          if (sizes.length > 0 && overLimitPrice) {
            oe.group_prices = "Price cannot exceed 9,999,999.99";
          }
        }
        if (Object.keys(oe).length) hasErr = true;
        const contactErrors = optionContactErrors(opt);
        if (Object.keys(contactErrors).length) {
          Object.assign(oe, contactErrors);
          hasErr = true;
        }
        optionErrors.push(oe);
      });
      if (hasErr) e.options = optionErrors;
    }
    return e;
  };

  const goNext = () => {
    const e = validateStep(step);
    if (Object.keys(e).length > 0) {
      setErrors(e);
      setValidationScrollVersion((version) => version + 1);
      toast.error(tr("Please fix the highlighted fields"));
      return;
    }
    setCompleted((c) => ({ ...c, [STEPS[step].key]: true }));
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const goBack = () => {
    setStep((s) => {
      const prev = Math.max(firstStep, s - 1);
      // Once Start has been completed, never let the user step back into
      // Acknowledgment (idx 0). Skip past it to the next visible step.
      if (prev === 0 && completed.acknowledge) return Math.max(prev, 1);
      // Once Start is completed, do allow visiting Start (it now shows the
      // locked chip), but not Acknowledgment.
      return prev;
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const jumpTo = (idx) => {
    if (idx === step) return;
    // Block jumping back to the hidden Acknowledgment + Start steps in edit mode.
    if (isEdit && idx <= 1) return;
    // Block jumping back to Acknowledgment once completed — its purpose is
    // a one-time gate, and the user has already confirmed.
    if (idx === 0 && completed.acknowledge) return;
    // Block jumping back to Start once completed — product type is locked.
    if (idx === 1 && completed.start) return;
    if (idx < step || completed[STEPS[idx].key]) {
      setStep(idx);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const buildPayload = (status, allowUntitled = false) => ({
    status,
    product_type: data.product_type,
    start_from: data.start_from,
    duplicate_source_id: data.duplicate_source_id || null,
    cloned_from: data.cloned_from || null,
    title:
      (data.title || "").trim() ||
      (allowUntitled && status === "draft" ? "Untitled product" : ""),
    reference_code: (data.reference_code || "").slice(0, PRODUCT_REFERENCE_CODE_MAX) || null,
    primary_city: (data.primary_city || "").trim() || null,
    short_description: data.short_description || null,
    full_description: data.full_description || null,
    highlights: (data.highlights || []).slice(0, HIGHLIGHT_COUNT),
    visit_locations: data.visit_locations || [],
    keywords: data.keywords || [],
    experience_leader: data.experience_leader || null,
    inclusions_tags: data.inclusions_tags || [],
    inclusions_other: normaliseBulletLines(data.inclusions_other) || null,
    not_included_tags: data.not_included_tags || [],
    not_included_other: normaliseBulletLines(data.not_included_other) || null,
    meals: mealsToApi(data.meals),
    transports: data.transports || [],
    not_suitable_for: data.not_suitable_for || [],
    suitability_tags: data.suitability_tags || [],
    guest_must_know: data.guest_must_know || null,
    refund_policy_type: data.refund_policy_type || "standard_24_hours",
    custom_refund_policy_value:
      data.refund_policy_type === "custom"
        ? Number(data.custom_refund_policy_value)
        : null,
    custom_refund_policy_unit:
      data.refund_policy_type === "custom"
        ? data.custom_refund_policy_unit || "Hour(s)"
        : null,
    // Send S3 keys for existing photos, data URLs for new uploads.
    // The wizard stores {key, url, name} for loaded photos and {url, name} for new ones.
    photos: (data.photos || []).map((p) =>
      typeof p === "string" ? p : (p.key || p.url)
    ),
    copyright_confirmed: !!data.copyright_confirmed,
    compliance_certified: !!data.compliance_certified,
    options: (data.options || []).map((opt) => ({
      ...opt,
      currency: partnerCurrency,
      has_schedule_end_date: !!opt.has_schedule_end_date,
      schedule_end_date: opt.has_schedule_end_date ? opt.schedule_end_date || "" : "",
      duration_value:
        opt.duration_value !== "" ? Number(opt.duration_value) : null,
      cutoff_minutes: Number(opt.cutoff_minutes ?? 60),
      // ---- Pricing ----
      capacity_min:
        opt.capacity_min !== "" && opt.capacity_min !== null
          ? Number(opt.capacity_min)
          : null,
      capacity_max:
        opt.capacity_max !== "" && opt.capacity_max !== null
          ? Number(opt.capacity_max)
          : null,
      flat_tiers: (opt.flat_tiers || []).map((t, index, tiers) => {
        const to = effectiveTierTo(tiers, index, opt.capacity_max);
        return {
          to: to !== "" && to !== null && to !== undefined ? Number(to) : null,
          price: t.price !== "" && t.price !== null ? Number(t.price) : null,
        };
      }),
      categories: (opt.categories || []).map((c) => ({
        ...c,
        age_max:
          c.age_max !== "" && c.age_max !== null ? Number(c.age_max) : null,
        tiers: (c.tiers || []).map((t, index, tiers) => {
          const to = effectiveTierTo(tiers, index, opt.capacity_max);
          return {
            to: to !== "" && to !== null && to !== undefined ? Number(to) : null,
            price: t.price !== "" && t.price !== null ? Number(t.price) : null,
          };
        }),
      })),
      group_sizes: (opt.group_sizes || []).map((s) => ({
        to: s.to !== "" && s.to !== null ? Number(s.to) : null,
      })),
      group_prices: Object.fromEntries(
        Object.entries(opt.group_prices || {})
          .filter(([, v]) => v !== "" && v !== null)
          .map(([k, v]) => [k, Number(v)]),
      ),
    })),
  });

  const submit = async (status, { showSuccess = true, allowUntitled = false } = {}) => {
    if (!(data.title || "").trim() && !allowUntitled) {
      toast.error(tr("Title is required"), {
        description: tr("Even a draft needs a title to find it later."),
      });
      setStep(1);
      return false;
    }
    if (status === "active" && !isPublishReady(data)) {
      const missing = missingPublishItems(data);
      toast.error(tr("Can't publish yet"), {
        description: missing.length > 0
          ? `Still needed: ${missing.join(", ")}.`
          : !data.compliance_certified
            ? "Please tick the compliance certification."
            : "Some required fields are missing.",
      });
      setStep(STEPS.length - 1);
      return false;
    }
    if (status === "active" && legalLoaded && !legalComplete) {
      toast.error(legalPublishMessage, {
        description: canManageLegal
          ? "We just need your company details. You can save this product as a draft in the meantime."
          : "You can save this product as a draft in the meantime.",
        action: canManageLegal
          ? {
              label: tr("Fill Legal"),
              onClick: () => navigate("/account/legal"),
            }
          : undefined,
      });
      return false;
    }
    setSubmittingStatus(status);
    setBusy(true);
    try {
      const payload = buildPayload(status, allowUntitled);
      payload.photos = await Promise.all(payload.photos.map(photo => uploadSellerImage(photo, "product")));
      const res = isEdit
        ? await axios.put(`${API}/products/${productId}`, payload)
        : await axios.post(`${API}/products`, payload);
      // Backend returns the full ProductDto on success (has `id` field)
      if (res.data?.id) {
        if (Array.isArray(res.data.photo_keys)) {
          setData(current => ({ ...current, photos: res.data.photo_keys.map((key, i) => ({
            key, url: res.data.photo_urls?.[i] ?? key,
          })) }));
        }
        if (showSuccess) setSuccessKind(status);
        // Clearing the dirty flag prevents the navigation blocker from
        // firing when the user clicks "Back to Products".
        setTouched(false);
        if (isEdit) setEditingStatus(status);
        return true;
      }
      return false;
    } catch (err) {
      const { code, message } = parseApiError(err, tr("Please review and try again."));
      if (code === "forbidden") {
        toast.error(legalPublishMessage, {
          description: canManageLegal
            ? "Your legal details must be submitted before going live."
            : "You can save this product as a draft in the meantime.",
          action: canManageLegal
            ? { label: tr("Fill Legal"), onClick: () => navigate("/account/legal") }
            : undefined,
        });
        return false;
      }
      toast.error(tr("Couldn't save"), { description: message });
      return false;
    } finally {
      setBusy(false);
      setSubmittingStatus(null);
    }
  };

  const ready = useMemo(() => isPublishReady(data), [data]);

  const StepBody = useMemo(() => {
    const props = { data, update, errors };
    // Once the user has advanced past the Start step the product_type choice
    // is locked — Step0Entry shows a read-only chip with a lock icon.
    const startLocked = isEdit || !!completed.start;
    switch (step) {
      case 0:
        return <StepAcknowledgment {...props} />;
      case 1:
        return <Step0Entry {...props} locked={startLocked} />;
      case 2:
        return <Step1Basics {...props} />;
      case 3:
        return <Step3Visuals {...props} />;
      case 4:
        return <Step2Option {...props} currency={partnerCurrency} />;
      case 5:
        return <Step5Review data={data} onJumpTo={jumpTo} onChange={update} />;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, data, errors, completed, partnerCurrency]);

  /* ---- Global save-or-discard gate ----
   * Triggered when the user has unsaved edits AND tries to leave the wizard
   * via any in-app navigation (header links, sidebar, breadcrumb, browser
   * back/forward) OR via a browser-level action (refresh / close tab). */
  const shouldBlock = touched && !successKind && !busy;
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      shouldBlock && currentLocation.pathname !== nextLocation.pathname,
  );
  const showCancelModal = blocker.state === "blocked";

  useEffect(() => {
    if (!shouldBlock) return undefined;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
      return "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [shouldBlock]);

  const handleKeepEditing = () => {
    if (blocker.state === "blocked") blocker.reset();
  };
  const handleDiscardLeave = () => {
    setTouched(false);
    if (blocker.state === "blocked") {
      blocker.proceed();
    } else {
      navigate("/products");
    }
  };
  const handleSaveBeforeLeave = async () => {
    const status = isEdit ? editingStatus || "draft" : "draft";
    const saved = await submit(status, {
      showSuccess: false,
      allowUntitled: !isEdit,
    });
    if (saved && blocker.state === "blocked") blocker.proceed();
  };

  const isLast = step === STEPS.length - 1;
  // Back is disabled at the literal first visible step, AND on the Start
  // step (idx 1) in create mode once Acknowledgment has been completed,
  // since Acknowledgment is a one-time gate the user can't return to.
  const isFirstVisible =
    step === firstStep || (!isEdit && step === 1 && !!completed.acknowledge);
  // Save-as-draft is available from the Basics step onwards in create mode
  // (i.e. steps 2..5 — Basics, Visuals, Option & pricing, Review). In edit
  // mode the label switches to "Save changes" and remains visible everywhere.
  const showSaveDraft = isEdit || step >= 2;

  // ---- Loading + error states (edit mode only) ----
  if (loading) {
    return (
      <main
        data-testid="edit-product-loading"
        className="mx-auto flex w-full max-w-[1100px] flex-1 items-center justify-center px-4 py-20 text-[#6B7280]"
      >
        <Loader2 className="me-3 h-5 w-5 animate-spin" />{tr("Loading product…")}</main>
    );
  }
  if (loadError) {
    return (
      <main
        data-testid="edit-product-error"
        className="mx-auto w-full max-w-[680px] flex-1 px-4 py-20 text-center"
      >
        <h1 className="text-2xl font-extrabold tracking-tight text-[#0F1115]">{tr("Couldn't load product")}</h1>
        <p className="mt-3 text-sm text-[#6B7280]">{loadError}</p>
        <button
          type="button"
          onClick={() => navigate("/products")}
          className="humrahe-btn-primary mt-6 inline-flex h-10 items-center rounded-md px-5 text-sm font-semibold"
        >{tr("Back to Products")}</button>
      </main>
    );
  }

  // Buttons in edit mode:
  //   - "Save changes"  → preserve editingStatus (PUT same status)
  //   - "Publish product" (only if editing a draft and on the Review step)
  const showPublishButton = !isEdit || editingStatus === "draft";
  const publishing = busy && submittingStatus === "active";
  const saveChangesLabel = isEdit ? tr("Save changes") : tr("Save as draft");
  const saveChangesStatus = isEdit ? editingStatus || "draft" : "draft";
  const showProductHeader = isEdit || step >= 2;

  return (
    <main
      data-testid={isEdit ? "edit-product-page" : "create-product-page"}
      className="mx-auto w-full max-w-[1100px] flex-1 px-4 py-8 sm:px-6 md:px-10"
    >
      <header className="flex flex-wrap items-start justify-between gap-3 pb-6">
        {showProductHeader ? (
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold uppercase tracking-wider text-[#6B7280]">
              {isEdit ? tr("Edit product") : tr("New product")}
            </p>
            <h1 className="break-words text-2xl font-extrabold leading-tight tracking-tight text-[#0F1115] md:text-[28px]">
              {data.title?.trim() || (isEdit ? tr("Untitled") : tr("Untitled product"))}
            </h1>
          </div>
        ) : (
          <div className="flex-1" aria-hidden="true" />
        )}
      </header>

      <div className="rounded-2xl border border-[#EAECEF] bg-white p-4 sm:p-6 md:p-8">
        <WizardStepper
          current={step}
          completed={completed}
          onJump={jumpTo}
          hideStart={isEdit}
        />
        {legalLoaded && !legalComplete && (
          <div
            data-testid="wizard-legal-warning"
            className="mt-5 flex flex-wrap items-center gap-3 rounded-md border border-[#FFD7D7] bg-[#FFF7F7] px-4 py-2.5 text-xs"
          >
            <span className="font-semibold text-[#9B0000]">{tr("You can save drafts now,")}</span>
            <span className="text-[#6B7280]">
              {canManageLegal
                ? tr("but publishing requires your Legal info.")
                : tr("but publishing requires an account admin to fill the legal information.")}
            </span>
            {canManageLegal && (
              <button
                type="button"
                onClick={() => navigate("/account/legal")}
                data-testid="wizard-legal-cta"
                className="ms-auto inline-flex h-7 items-center rounded-md border border-[#FFD7D7] bg-white px-2.5 text-[11px] font-semibold text-[#9B0000] transition-colors hover:border-[#9B0000]"
              >{tr("Fill Legal info")}</button>
            )}
          </div>
        )}
        <div className="mt-6 sm:mt-8">{StepBody}</div>
      </div>

      <footer
        data-testid="wizard-footer"
        className="sticky bottom-0 start-0 end-0 z-30 mt-6 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-[#EAECEF] bg-white/95 p-4 shadow-[0_-2px_24px_-12px_rgba(15,17,21,0.18)] backdrop-blur"
      >
        {step === 0 ? (
          <span aria-hidden />
        ) : (
          <button
            type="button"
            onClick={goBack}
            disabled={isFirstVisible || busy}
            data-testid="wizard-back"
            className={cn(
              "inline-flex h-10 items-center gap-1.5 rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115]",
              (isFirstVisible || busy) && "cursor-not-allowed opacity-40 hover:border-[#E6E8EC]",
            )}
          >
            <ArrowLeft className="h-4 w-4" />{tr("Back")}</button>
        )}

        <div className="flex flex-wrap items-center gap-2">
          {showSaveDraft && (
            <button
              type="button"
              onClick={() => submit(saveChangesStatus)}
              disabled={busy}
              data-testid="wizard-save-draft"
              className="inline-flex h-10 items-center gap-1.5 rounded-md border border-[#E6E8EC] px-4 text-sm font-semibold text-[#0F1115] transition-colors hover:border-[#0F1115] disabled:cursor-not-allowed disabled:opacity-60"
            >
              <Save className="h-4 w-4" />
              {saveChangesLabel}
            </button>
          )}
          {!isLast ? (
            <button
              type="button"
              onClick={goNext}
              disabled={busy}
              data-testid="wizard-next"
              className="humrahe-btn-primary inline-flex h-10 items-center gap-1.5 rounded-md px-5 text-sm font-semibold disabled:opacity-60"
            >{tr("Continue")}<ArrowRight className="h-4 w-4" />
            </button>
          ) : showPublishButton ? (
            <button
              type="button"
              onClick={() => submit("active")}
              disabled={busy || !ready}
              data-testid="wizard-publish"
              title={
                !legalComplete
                  ? canManageLegal
                    ? tr("Complete your Legal info to enable publishing — drafts work fine in the meantime")
                    : tr("Ask your account admin to fill the legal information to publish the product.")
                  : !ready
                    ? tr("Complete all required fields and tick the compliance box to publish")
                    : undefined
              }
              className={cn(
                "inline-flex h-10 items-center gap-1.5 rounded-md bg-[#2476FF] px-5 text-sm font-semibold text-white transition-colors",
                ready ? "hover:bg-[#1857c4]" : "cursor-not-allowed opacity-50",
                busy && "cursor-not-allowed opacity-60",
                !legalComplete && "cursor-not-allowed opacity-50",
              )}
            >
              {publishing ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Sparkles className="h-4 w-4" strokeWidth={2.4} />
              )}
              <span aria-live="polite">
                {publishing ? tr("Publishing…") : tr("Publish product")}
              </span>
            </button>
          ) : null}
        </div>
      </footer>

      <SuccessModal
        open={!!successKind}
        kind={successKind}
        mode={mode}
        onClose={() => setSuccessKind(null)}
        onGoToProducts={() => navigate("/products")}
      />
      <ConfirmCancelModal
        open={showCancelModal}
        onDismiss={handleKeepEditing}
        onDiscard={handleDiscardLeave}
        onSave={handleSaveBeforeLeave}
        saving={busy}
        isEdit={isEdit}
      />
    </main>
  );
}
